'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { StaffMember, ServiceTemplate, ServiceCategory, Ticket } from '@/lib/types';
import { supabaseBrowser, resetSupabaseClient } from '@/lib/supabase-browser';
import { mapCategoryRow, mapServiceRow, aliasServiceAreas } from '@/lib/mappers';
import { ticketChangeAction, createTicketRefetcher, fetchTicketForList, applyRefetchedTicket, type TicketEventSource } from '@/lib/ticket-realtime';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { reportError } from '@/lib/error-reporting';
import { kickToLogin, reloadStaleClient } from '@/lib/session-client';
import { clientEpochHeaders, STALE_CLIENT_CODE, STALE_CLIENT_STATUS } from '@/lib/client-epoch';

// ── API helpers ──
async function api<T>(url: string, method = 'GET', body?: any): Promise<T> {
  // clientEpochHeaders: /api/data gates on it (src/lib/client-epoch.ts); the
  // other routes ignore it.
  const opts: RequestInit = { method, headers: { 'Content-Type': 'application/json', ...clientEpochHeaders() } };
  if (body) opts.body = JSON.stringify(body);

  let res: Response;
  try {
    res = await fetch(url, opts);
  } catch (networkErr) {
    // Network failure (offline, DNS, CORS, etc.)
    reportError(networkErr, {
      context: 'api:network',
      extra: { url, method },
    });
    throw networkErr;
  }

  if (!res.ok) {
    let msg = `API error: ${res.status}`;
    let code: string | undefined;
    try {
      const err = await res.json();
      msg = err.error || err.message || err.details || msg;
      if (typeof err.code === 'string') code = err.code;
    } catch {}
    // Attach status/code so callers can branch on specific failure
    // modes (e.g. STAFF-001 username collision = 409 + code:'username_taken').
    const apiErr = Object.assign(new Error(msg), { status: res.status, code });
    reportError(apiErr, {
      context: 'api:response',
      extra: { url, method, status: res.status },
    });
    throw apiErr;
  }
  return res.json();
}

// ── Context ──
/** What PATCH /api/tickets hands back. `splits_drift` is present only when a
 *  post-close edit left tickets.total disagreeing with the payment breakdown
 *  captured at cobro (COBRO-SPLITS-1). Nothing else in the app reads that
 *  column, so this is the only way staff can be told the charge and the
 *  ticket have parted ways. */
export interface TicketPatchResult {
  success?: boolean;
  unredeemed_packages?: string[];
  redeemed_packages?: string[];
  splits_drift?: {
    status: 'amount' | 'methods';
    charged: number | null;
    gap: number | null;
    total: number;
    totalBefore: number;
    storedMethods: string[];
    currentMethods: string[];
  } | null;
}

interface AppData {
  // Data
  staff: StaffMember[];
  services: ServiceTemplate[];
  categories: ServiceCategory[];
  tickets: Ticket[];
  settings: Record<string, string>;
  usdRate: number;
  loading: boolean;
  loadError: Error | null;
  user: { id: string; name: string; role: string; system_role: string; initials: string; color: string; isAdmin: boolean; avatar_url?: string } | null;
  userLoading: boolean;
  clinicId: string | null;

  // Staff CRUD
  addStaff: (data: Partial<StaffMember>) => Promise<void>;
  updateStaff: (id: string, data: Partial<StaffMember>) => Promise<void>;
  removeStaff: (id: string) => Promise<void>;

  // Category CRUD
  addCategory: (data: { name: string; order: number }) => Promise<void>;
  updateCategory: (id: string, data: { name: string }) => Promise<void>;
  removeCategory: (id: string, migrateTo?: string) => Promise<void>;
  setCategories: (cats: ServiceCategory[]) => void;

  // Service CRUD
  addService: (data: Partial<ServiceTemplate>) => Promise<void>;
  updateService: (id: string, data: Partial<ServiceTemplate>) => Promise<void>;
  removeService: (id: string) => Promise<void>;

  // Ticket CRUD
  addTicket: (data: Partial<Ticket>) => Promise<Ticket>;
  updateTicketData: (id: string, data: Partial<Ticket>) => Promise<TicketPatchResult>;
  removeTicket: (id: string) => Promise<void>;

  // Helpers
  getStaffServices: (staffId: string) => ServiceTemplate[];
  updateSetting: (key: string, value: string) => Promise<void>;
  /** Bulk variant — writes many settings keys in one round-trip.
   *  Single-key edits should keep using updateSetting; this is for
   *  import / reset-to-defaults / multi-field saves. */
  updateSettingsBulk: (patch: Record<string, string>) => Promise<void>;
  refresh: () => Promise<void>;
  /** Targeted refetch of just the staff list — used by the equipo
   *  realtime channel so a row INSERT/UPDATE/DELETE doesn't have to
   *  re-pull tickets + services + settings via the global refresh(). */
  refreshStaff: () => Promise<void>;
}

const AppDataContext = createContext<AppData | null>(null);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [services, setServices] = useState<ServiceTemplate[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [usdRate, setUsdRate] = useState<number>(20.5);
  const [clinicId, setClinicId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [user, setUser] = useState<AppData['user']>(null);
  const [userLoading, setUserLoading] = useState(true);

  // Track in-flight mutations so refresh() doesn't overwrite optimistic state
  const deletedIds = useRef(new Set<string>());
  const pendingTickets = useRef(new Map<string, Ticket>());
  const updatedTickets = useRef(new Map<string, Partial<Ticket>>());

  // Auth check — runs once
  useEffect(() => {
    fetch('/api/auth').then(r => {
      if (!r.ok) return null;
      return r.json();
    }).then(d => { if (d?.user) setUser(d.user); }).catch((err) => {
      console.error('Auth check failed (network error):', err);
      reportError(err, { context: 'auth:check' });
    }).finally(() => setUserLoading(false));
  }, []);

  // Reset Supabase client when user changes (picks up fresh JWT cookie)
  useEffect(() => {
    resetSupabaseClient();
  }, [user?.id]);

  const refresh = useCallback(async () => {
    try {
      const data = await api<any>('/api/data');
      if (data.clinic_id) setClinicId(data.clinic_id);
      setStaff(data.staff);
      // /api/data ships `zonas` only; restore the legacy `areas` alias the
      // ticket composer and Builder still read (see stripServiceAreasForWire).
      setServices(aliasServiceAreas(data.services));
      setCategories(data.categories);
      // Merge tickets: exclude deleted IDs, include pending additions, apply pending updates
      let serverTickets: Ticket[] = data.tickets.filter((t: Ticket) => !deletedIds.current.has(t.id));
      // Apply any pending updates (optimistic state that API might not have persisted yet)
      if (updatedTickets.current.size > 0) {
        serverTickets = serverTickets.map((t: Ticket) => {
          const update = updatedTickets.current.get(t.id);
          return update ? { ...t, ...update } as Ticket : t;
        });
      }
      const pending = Array.from(pendingTickets.current.values()).filter(t => !serverTickets.some(st => st.id === t.id));
      setTickets([...pending, ...serverTickets]);
      setSettings(data.settings);
      setLoadError(null);
      // Fetch exchange rate (non-blocking)
      api<{ rate: number }>('/api/exchange-rate')
        .then(r => { if (r?.rate) setUsdRate(r.rate); })
        .catch((e) => reportError(e, { context: 'exchange-rate' }));
    } catch (e) {
      // RT-1: a 401 here means the session cookie is gone (tab slept past
      // the 12h window before the keepalive could slide it). The app used
      // to keep rendering stale data with every poll silently failing —
      // send the user to login instead of a frozen-but-alive-looking UI.
      // The login page lives at '/' (src/app/page.tsx) — '/login' is only a
      // legacy public-path entry with NO page behind it (404). Mirror the
      // proxy's own redirect shape (see buildLoginRedirect in proxy.ts):
      // '/' plus ?next= so staff lands back where they were after login.
      //
      // NEVER redirect while ALREADY on '/': this provider mounts in the
      // root layout, so it runs on the login page too, where a 401 is the
      // EXPECTED state for a logged-out visitor — redirecting there caused
      // an infinite full-page reload loop ('/' → 401 → assign('/') → …,
      // the "page blinks" incident). On '/', fall through to the silent
      // pre-RT-1 behavior.
      // Routed through session-client so this kick and the heartbeat's
      // share ONE loop guard + ONE login target, and so a kick also clears
      // sb_jwt (DELETE /api/auth) instead of leaving the PHI-read token in
      // document.cookie. kickToLogin itself no-ops on '/'.
      if ((e as { status?: number })?.status === 401
          && typeof window !== 'undefined'
          && window.location.pathname !== '/') {
        void kickToLogin('data-401');
        return;
      }
      // Client-epoch gate (src/lib/client-epoch.ts): this bundle is older than
      // the server's CLIENT_EPOCH — reload onto the new one (same session).
      if ((e as { status?: number; code?: string })?.status === STALE_CLIENT_STATUS
          && (e as { code?: string })?.code === STALE_CLIENT_CODE
          && reloadStaleClient('data-409')) {
        return;
      }
      console.error('Failed to load data:', e);
      reportError(e, { context: 'data:initial-load' });
      setLoadError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh when tab becomes visible — with 5 minute cooldown
  const hiddenAtRef = useRef<number>(0);
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
      } else {
        const elapsed = Date.now() - hiddenAtRef.current;
        if (hiddenAtRef.current > 0 && elapsed > 5 * 60 * 1000) {
          refresh();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [refresh]);

  // Ids currently in the list — read by the realtime handlers (their effect
  // is not keyed on `tickets`) to skip events for historical tickets.
  const ticketIdsRef = useRef(new Set<string>());
  useEffect(() => { ticketIdsRef.current = new Set(tickets.map(t => t.id)); }, [tickets]);

  // RT-2: this channel is NOT keyed on the realtime JWT any more. The browser
  // client survives sb_jwt rotation (supabase-browser pushes the new token
  // onto the live socket), so there is nothing to rebuild — and rebuilding
  // would be actively harmful: RealtimeClient.channel(topic) returns the
  // still-leaving old channel, whose subscribe() no-ops. subscribedOnce still
  // skips only the FIRST ever SUBSCRIBED (the mount refresh already loaded);
  // a genuine socket drop rejoins and refetches.
  const subscribedOnce = useRef(false);

  // ── Realtime subscriptions ──
  useEffect(() => {
    if (!initialLoaded || !clinicId) return;

    function handleSimpleChange<T extends { id: string }>(
      payload: RealtimePostgresChangesPayload<{ [key: string]: any }>,
      setter: React.Dispatch<React.SetStateAction<T[]>>,
      mapper: (row: any) => T
    ) {
      if (payload.eventType === 'INSERT') {
        const item = mapper(payload.new);
        setter(prev => prev.some(p => p.id === item.id) ? prev : [...prev, item]);
      } else if (payload.eventType === 'UPDATE') {
        const item = mapper(payload.new);
        setter(prev => prev.map(p => p.id === item.id ? item : p));
      } else if (payload.eventType === 'DELETE') {
        const id = payload.old?.id;
        if (id) setter(prev => prev.filter(p => p.id !== id));
      }
    }

    // tickets + ticket_services events are NUDGES (sb_jwt Phase 1): the
    // browser token reads only id/clinic_id/status/deleted/patient_id/closed_at
    // and id/clinic_id/ticket_id, so the payload is not the ticket. Each event
    // queues a debounced per-ticket refetch through GET /api/tickets/[id]
    // (role-checked, same mapping as /api/data). See src/lib/ticket-realtime.ts.
    const refetcher = createTicketRefetcher({
      fetchTicket: (id) => fetchTicketForList(id),
      apply: (id, ticket, allowInsert) => {
        // Mark a just-inserted ticket as known NOW: ticketIdsRef is otherwise
        // refreshed only by the effect after the next render, and a line event
        // landing in between would be dropped as "historical" (extra ids here
        // only cost a refetch; the effect resets the set on render).
        if (ticket === null) ticketIdsRef.current.delete(id);
        else if (allowInsert) ticketIdsRef.current.add(id);
        setTickets(prev => applyRefetchedTicket(prev, id, ticket, allowInsert, {
          isDeleted: (x) => deletedIds.current.has(x),
          isPending: (x) => pendingTickets.current.has(x),
          pendingUpdate: (x) => updatedTickets.current.get(x),
        }));
      },
    });
    const listView = {
      isKnown: (id: string) => ticketIdsRef.current.has(id),
      isDeleted: (id: string) => deletedIds.current.has(id),
      isPending: (id: string) => pendingTickets.current.has(id),
      isScheduled: (id: string) => refetcher.isScheduled(id),
    };
    function handleTicketEvent(source: TicketEventSource, payload: RealtimePostgresChangesPayload<{ [key: string]: any }>) {
      const action = ticketChangeAction(source, payload, listView);
      if (action.kind === 'remove') setTickets(prev => prev.filter(t => t.id !== action.id));
      else if (action.kind === 'refetch') refetcher.schedule(action.id, action.allowInsert);
    }

    function handleSettingsChange(payload: RealtimePostgresChangesPayload<{ [key: string]: any }>) {
      if (payload.eventType === 'DELETE') {
        const key = payload.old?.key;
        if (key) setSettings(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      } else {
        const { key, value } = payload.new;
        if (key) setSettings(prev => ({ ...prev, [key]: value }));
      }
    }

    const clinicFilter = `clinic_id=eq.${clinicId}`;
    const channel = supabaseBrowser
      .channel('app-data-sync')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'categories', filter: clinicFilter },
        (payload) => handleSimpleChange(payload, setCategories, mapCategoryRow))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'services', filter: clinicFilter },
        (payload) => handleSimpleChange(payload, setServices, mapServiceRow))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tickets', filter: clinicFilter },
        (payload) => handleTicketEvent('tickets', payload))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ticket_services', filter: clinicFilter },
        (payload) => handleTicketEvent('ticket_services', payload))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'settings', filter: clinicFilter },
        (payload) => handleSettingsChange(payload))
      .subscribe((status) => {
        // RT-1: after a dropped-then-rejoined socket, refetch so rows missed
        // during the gap appear (the per-row handlers only see live events).
        // Skip the very first SUBSCRIBED — the mount refresh already loaded.
        if (status === 'SUBSCRIBED') {
          if (subscribedOnce.current) refresh();
          else subscribedOnce.current = true;
        }
      });

    return () => {
      supabaseBrowser.removeChannel(channel);
      // Drop queued ticket refetches (and any result still in flight)
      refetcher.dispose();
    };
  }, [initialLoaded, clinicId]);

  // ── Staff ──
  const addStaff = async (data: Partial<StaffMember>) => {
    const result = await api<StaffMember>('/api/staff', 'POST', data);
    setStaff(prev => [...prev, result]);
  };

  const updateStaff = async (id: string, data: Partial<StaffMember>) => {
    await api('/api/staff', 'PATCH', { record_id: id, ...data });
    setStaff(prev => prev.map(s => s.id === id ? { ...s, ...data } as StaffMember : s));
  };

  const removeStaff = async (id: string) => {
    await api('/api/staff', 'DELETE', { record_id: id });
    setStaff(prev => prev.filter(s => s.id !== id));
  };

  // ── Categories ──
  const addCategory = async (data: { name: string; order: number }) => {
    const result = await api<ServiceCategory>('/api/categories', 'POST', data);
    setCategories(prev => [...prev, result]);
  };

  const updateCategoryFn = async (id: string, data: { name: string }) => {
    const old = categories.find(c => c.id === id);
    await api('/api/categories', 'PATCH', { id, ...data });
    setCategories(prev => prev.map(c => c.id === id ? { ...c, ...data } : c));
    // Also update local services that reference the old category name
    if (old && old.name !== data.name) {
      setServices(prev => prev.map(s => s.category === old.name ? { ...s, category: data.name } : s));
    }
  };

  const removeCategory = async (id: string, migrateTo?: string) => {
    await api('/api/categories', 'DELETE', { record_id: id, migrate_to: migrateTo });
    setCategories(prev => prev.filter(c => c.id !== id));
    if (migrateTo) {
      const old = categories.find(c => c.id === id);
      if (old) setServices(prev => prev.map(s => s.category === old.name ? { ...s, category: migrateTo } : s));
    }
  };

  // ── Services ──
  const addService = async (data: Partial<ServiceTemplate>) => {
    const result = await api<ServiceTemplate>('/api/services', 'POST', data);
    setServices(prev => [...prev, result]);
  };

  const updateServiceFn = async (id: string, data: Partial<ServiceTemplate>) => {
    await api('/api/services', 'PATCH', { record_id: id, ...data });
    setServices(prev => prev.map(s => s.id === id ? { ...s, ...data } as ServiceTemplate : s));
  };
  const removeService = async (id: string) => {
    await api('/api/services', 'DELETE', { record_id: id });
    setServices(prev => prev.filter(s => s.id !== id));
  };

  // ── Tickets ──
  const addTicket = async (data: Partial<Ticket>) => {
    const result = await api<Ticket & { reused?: boolean }>('/api/tickets', 'POST', data);
    if (result.reused) {
      // The server coalesced this creation onto the patient's existing
      // open ticket (duplicate guard) and returned it fully mapped,
      // services included. REPLACE any copy already in the list — if
      // we're here the list was likely stale, and the authoritative row
      // must win before the register reads it. No pending marker: the
      // row already exists server-side, refresh can't lose it.
      setTickets(prev => prev.some(t => t.id === result.id)
        ? prev.map(t => (t.id === result.id ? result : t))
        : [result, ...prev]);
      return result;
    }
    // Track as pending so refresh doesn't lose it before DB propagates
    pendingTickets.current.set(result.id, result);
    setTickets(prev => prev.some(t => t.id === result.id) ? prev : [result, ...prev]);
    // Clear from pending after DB has propagated (next refresh will include it)
    setTimeout(() => { pendingTickets.current.delete(result.id); }, 10000);
    // The hero cobro flow routes straight into /tickets/cobrar/[id].
    return result;
  };

  const updateTicketData = async (id: string, data: Partial<Ticket>): Promise<TicketPatchResult> => {
    // Save previous state for rollback
    const prev = tickets.find(t => t.id === id);
    // Optimistic update
    setTickets(p => p.map(t => t.id === id ? { ...t, ...data } as Ticket : t));
    updatedTickets.current.set(id, data);
    try {
      // The response body carries splits_drift when this edit left the
      // ticket billing an amount the payment breakdown does not match.
      // Return it so the panel can warn — the column has no UI of its own.
      const res = await api<TicketPatchResult>('/api/tickets', 'PATCH', { record_id: id, ...data });
      setTimeout(() => { updatedTickets.current.delete(id); }, 5000);
      return res;
    } catch (e) {
      console.error('Update failed, rolling back:', e);
      reportError(e, { context: 'ticket:update-rollback' });
      // Rollback
      if (prev) setTickets(p => p.map(t => t.id === id ? prev : t));
      updatedTickets.current.delete(id);
      throw e; // Re-throw so callers can show error UI
    }
  };

  const removeTicket = async (id: string) => {
    const prevTicket = tickets.find(t => t.id === id);
    deletedIds.current.add(id);
    setTickets(p => p.filter(t => t.id !== id));
    try {
      await api('/api/tickets', 'DELETE', { id });
      setTimeout(() => { deletedIds.current.delete(id); }, 10000);
    } catch (e) {
      console.error('Delete failed, rolling back:', e);
      reportError(e, { context: 'ticket:delete-rollback' });
      deletedIds.current.delete(id);
      if (prevTicket) setTickets(p => [prevTicket, ...p]);
      throw e; // Re-throw so callers can show error UI
    }
  };

  // ── Helpers ──
  const getStaffServices = (staffId: string) => {
    return services.filter(s => s.staff_ids?.includes(staffId) && s.active);
  };

  const updateSettingFn = async (key: string, value: string) => {
    await api('/api/settings', 'PATCH', { key, value });
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateSettingsBulkFn = async (patch: Record<string, string>) => {
    if (!patch || Object.keys(patch).length === 0) return;
    await api('/api/settings/bulk', 'PATCH', { patch });
    setSettings(prev => ({ ...prev, ...patch }));
  };

  const refreshStaff = useCallback(async () => {
    try {
      const data = await api<StaffMember[]>('/api/staff');
      if (Array.isArray(data)) setStaff(data);
    } catch (e) {
      reportError(e, { context: 'refreshStaff' });
    }
  }, []);

  return (
    <AppDataContext.Provider value={{
      staff, services, categories, tickets, settings, usdRate, loading, loadError, user, userLoading, clinicId,
      addStaff, updateStaff, removeStaff,
      addCategory, updateCategory: updateCategoryFn, removeCategory, setCategories,
      addService, updateService: updateServiceFn, removeService,
      addTicket, updateTicketData, removeTicket,
      getStaffServices, updateSetting: updateSettingFn, updateSettingsBulk: updateSettingsBulkFn, refresh, refreshStaff,
    }}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used within AppDataProvider');
  return ctx;
}
